import { useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, TextField,
  Typography, alpha,
} from '@mui/material';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  excludePVCashflow, getPVCashflow, includePVCashflow,
} from '../services/projectVitalCashflowService';
import type {
  PVCashflowCandidate, PVCashflowPlanOccurrence, PVCashflowWrite,
} from '../types/projectVitalCashflow';

const CYAN = '#4DD0E1';
const GREEN = '#4ade80';
const RED = '#fca5a5';
const YELLOW = '#fcd34d';

const money = (value: number | string) => new Intl.NumberFormat('es-ES', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
}).format(Number(value));

function todayLocal(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
    .toISOString().slice(0, 10);
}

function errorMessage(error: unknown): string {
  const candidate = error as { response?: { data?: { detail?: string } } };
  return candidate?.response?.data?.detail || 'No se pudo guardar la proyección.';
}

type Editor = { kind: PVCashflowCandidate['kind']; id: string; name: string; movement_type: PVCashflowCandidate['movement_type'] };

/**
 * Proyección CONSOLIDADA; no añade facturas ni reservas automáticamente.
 * Solo cuando el usuario incorpora una fuente se añade a la simulación.
 * Puede sustituir una ocurrencia manual concreta y evitar el doble cómputo.
 */
export default function ProjectVitalProfessionalCashflowSection() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [days, setDays] = useState(60);
  const [onlyPending, setOnlyPending] = useState(false);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [form, setForm] = useState<PVCashflowWrite>({
    expected_date: todayLocal(), account_id: '', linked_plan_id: null,
    linked_occurrence_date: null,
  });
  const [formError, setFormError] = useState('');

  const reportQuery = useQuery({
    queryKey: ['project-vital', 'cashflow', days],
    queryFn: () => getPVCashflow(days),
  });
  const report = reportQuery.data;
  const candidates = useMemo(() => (report?.candidates ?? [])
    .filter((item) => !onlyPending || !item.projection)
    .sort((a, b) => (a.due_date ?? '9999-12-31').localeCompare(b.due_date ?? '9999-12-31')),
  [report, onlyPending]);
  const planOptions = useMemo(() => {
    if (!report || !editor) return [];
    const result = report.available_plans.filter((item) => item.movement_type === editor.movement_type);
    const current = report.inclusions.find((item) => item.source_kind === editor.kind && item.source_id === editor.id);
    if (current?.linked_plan_id && current.linked_occurrence_date &&
        !result.some((option) => option.plan_id === current.linked_plan_id &&
          option.occurrence_date === current.linked_occurrence_date)) {
      result.unshift({
        plan_id: current.linked_plan_id,
        occurrence_date: current.linked_occurrence_date,
        name: 'Vinculación actual', amount: 0, movement_type: editor.movement_type,
        financial_scope: 'professional',
      });
    }
    return result;
  }, [report, editor]);
  const mutation = useMutation({
    mutationFn: () => {
      if (!editor) throw new Error('Selecciona una fuente pendiente.');
      return includePVCashflow(editor.kind, editor.id, form);
    },
    onSuccess: async () => {
      setEditor(null);
      setFormError('');
      await queryClient.invalidateQueries({ queryKey: ['project-vital', 'cashflow'] });
      enqueueSnackbar('Proyección actualizada; no se creó un movimiento real.', { variant: 'success' });
    },
    onError: (error) => setFormError(errorMessage(error)),
  });
  const exclusion = useMutation({
    mutationFn: ({ kind, id }: { kind: PVCashflowCandidate['kind']; id: string }) =>
      excludePVCashflow(kind, id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['project-vital', 'cashflow'] });
      enqueueSnackbar('Fuente retirada de la proyección.', { variant: 'success' });
    },
    onError: (error) => enqueueSnackbar(errorMessage(error), { variant: 'error' }),
  });

  function openEditor(item: PVCashflowCandidate) {
    const cfg = item.projection;
    const futureDate = item.due_date && item.due_date >= todayLocal()
      ? item.due_date : todayLocal();
    setEditor({ kind: item.kind, id: item.id, name: item.name, movement_type: item.movement_type });
    setForm({
      expected_date: cfg?.expected_date ?? futureDate,
      account_id: cfg?.account_id ?? item.account_id ?? report?.accounts.find(
        (account) => account.financial_scope === 'professional',
      )?.id ?? report?.accounts[0]?.id ?? '',
      linked_plan_id: cfg?.linked_plan_id ?? null,
      linked_occurrence_date: cfg?.linked_occurrence_date ?? null,
    });
    setFormError('');
  }

  function selectedPlan(value: string) {
    if (!value) {
      setForm((current) => ({ ...current, linked_plan_id: null, linked_occurrence_date: null }));
      return;
    }
    const chosen = planOptions.find((item) => `${item.plan_id}|${item.occurrence_date}` === value);
    if (chosen) {
      setForm((current) => ({ ...current, linked_plan_id: chosen.plan_id,
        linked_occurrence_date: chosen.occurrence_date }));
    }
  }

  if (reportQuery.isPending) return <CircularProgress size={22} sx={{ my: 2 }} />;
  if (reportQuery.isError || !report) return (
    <Alert severity="error" action={<Button onClick={() => void reportQuery.refetch()}>Reintentar</Button>}>
      No se pudo cargar la proyección consolidada. Comprueba que el backend y la nueva migración están desplegados.
    </Alert>
  );

  const summaries = [
    { title: 'Liquidez actual global', amount: report.current_liquidity, color: '#f8fafc' },
    { title: `Proyección a ${days} días`, amount: report.projected_liquidity, color: CYAN },
    { title: 'Entradas previstas', amount: report.forecast_income, color: GREEN },
    { title: 'Salidas previstas', amount: report.forecast_expenses, color: RED },
  ];
  const totalNotIncluded = report.unscheduled.invoices_income +
    report.unscheduled.invoices_expenses + report.unscheduled.reserves;
  const closed = report.inclusions.filter((config) => !report.candidates.some(
    (candidate) => candidate.kind === config.source_kind && candidate.id === config.source_id,
  ));
  const events = showAllEvents ? report.timeline : report.timeline.slice(0, 20);
  const saveDisabled = mutation.isPending || !form.account_id || !form.expected_date;

  return (
    <Box sx={{ mt: 2.4, pt: 2, borderTop: `1px solid ${alpha('#94a3b8', .14)}` }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }}
        justifyContent="space-between" gap={1.2}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <TimelineRoundedIcon sx={{ color: CYAN }} fontSize="small" />
            <Typography sx={{ color: '#f8fafc', fontWeight: 850, fontSize: '1.08rem' }}>
              Proyección consolidada
            </Typography>
          </Stack>
          <Typography sx={{ color: '#94a3b8', fontSize: '.8rem', mt: .4 }}>
            Incluye previsiones manuales y las facturas o reservas que incorpores expresamente.
            No supone que todas las facturas se cobren a tiempo.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {[30, 60, 90].map((value) => (
            <Button key={value} size="small" variant={days === value ? 'contained' : 'outlined'}
              onClick={() => setDays(value)}>{value} días</Button>
          ))}
        </Stack>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1, mt: 1.5 }}>
        {summaries.map((item) => (
          <Box key={item.title} sx={{ minWidth: 0, p: 1.2, bgcolor: '#19273b',
            borderRadius: 2, border: `1px solid ${alpha('#94a3b8', .12)}` }}>
            <Typography sx={{ fontSize: '.72rem', color: '#94a3b8' }}>{item.title}</Typography>
            <Typography sx={{ fontSize: { xs: '1.1rem', md: '1.3rem' }, fontWeight: 850,
              overflowWrap: 'anywhere', color: item.color }}>{money(item.amount)}</Typography>
          </Box>
        ))}
      </Box>
      {report.lowest_liquidity < 0 && (
        <Alert severity="warning" sx={{ mt: 1.2 }}>
          Según las fechas introducidas, la liquidez global podría bajar de cero.
          Mínimo simulado: {money(report.lowest_liquidity)}.
        </Alert>
      )}
      {(totalNotIncluded > 0 || report.unscheduled.overdue_count > 0) && (
        <Alert severity="info" sx={{ mt: 1.3 }}>
          Sin incorporar a esta proyección: {money(report.unscheduled.invoices_income)} por cobrar,
          {' '}{money(report.unscheduled.invoices_expenses)} por pagar y
          {' '}{money(report.unscheduled.reserves)} en reservas.
          {report.unscheduled.overdue_count > 0 ? ` ${report.unscheduled.overdue_count} vencidas sin fecha futura asignada.` : ''}
          {' '}No se suman hasta que las incorpores para evitar duplicidades.
        </Alert>
      )}
      {report.excluded_plan_occurrences > 0 && (
        <Typography sx={{ fontSize: '.76rem', color: '#94a3b8', mt: 1.3 }}>
          {report.excluded_plan_occurrences} ocurrencia(s) manuales sustituidas por vínculos explícitos.
        </Typography>
      )}
      {report.ignored_configuration_count > 0 && (
        <Alert severity="warning" sx={{ mt: 1.2 }}>
          Hay {report.ignored_configuration_count} configuración(es) con origen o cuenta no disponibles.
          Revisa las fuentes vinculadas.
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} mt={2} mb={1} gap={1}
        justifyContent="space-between" alignItems={{ sm: 'center' }}>
        <Typography sx={{ fontWeight: 800 }}>Incorporar compromisos ({report.candidates.length})</Typography>
        <Button size="small" onClick={() => setOnlyPending((value) => !value)}>
          {onlyPending ? 'Mostrar todos' : 'Solo no incorporados'}
        </Button>
      </Stack>
      <Typography sx={{ color: '#94a3b8', fontSize: '.77rem', mb: 1 }}>
        Si ya has planificado un cobro o gasto en «Previsión y recurrentes»,
        selecciona la ocurrencia correspondiente al incorporarlo: sustituirá esa previsión, sin sumarse dos veces.
      </Typography>
      {candidates.length === 0 && (
        <Typography sx={{ color: '#94a3b8', fontSize: '.83rem', my: 1 }}>
          No hay compromisos pendientes con este filtro.
        </Typography>
      )}
      <Stack spacing={.7}>
        {candidates.slice(0, 60).map((item) => (
          <Stack key={`${item.kind}:${item.id}`} direction={{ xs: 'column', sm: 'row' }}
            spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between"
            sx={{ px: 1.2, py: .9, bgcolor: '#19273b', borderRadius: 2,
              border: `1px solid ${alpha('#94a3b8', .11)}` }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ color: '#f8fafc', fontSize: '.84rem', fontWeight: 750,
                overflowWrap: 'anywhere' }}>
                {item.kind === 'invoice' ? 'Factura' : 'Reserva'} · {item.name}
              </Typography>
              <Typography sx={{ fontSize: '.74rem', color: '#94a3b8' }}>
                {item.due_date ? `Vence ${item.due_date}` : 'Sin vencimiento'} · {item.movement_type === 'ingreso' ? 'Entrada' : 'Salida'}
                {item.projection ? ` · Proyectada ${item.projection.expected_date}` : ' · No incorporada'}
              </Typography>
            </Box>
            <Typography sx={{ fontWeight: 800, color: item.movement_type === 'ingreso' ? GREEN : RED }}>
              {item.movement_type === 'ingreso' ? '+' : '−'}{money(item.amount)}
            </Typography>
            <Stack direction="row" spacing={.5}>
              <Button size="small" variant="outlined" onClick={() => openEditor(item)}>
                {item.projection ? 'Editar fecha' : 'Incorporar'}
              </Button>
              {item.projection && <Button size="small" color="warning" disabled={exclusion.isPending}
                onClick={() => exclusion.mutate({ kind: item.kind, id: item.id })}>Excluir</Button>}
            </Stack>
          </Stack>
        ))}
      </Stack>
      {candidates.length > 60 && <Alert severity="info" sx={{ mt: 1 }}>
        Se muestran las primeras 60 fuentes pendientes, ordenadas por vencimiento.
      </Alert>}
      {closed.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Button size="small" onClick={() => setShowClosed((v) => !v)}>
            {showClosed ? 'Ocultar vínculos finalizados' : `Vínculos de fuentes finalizadas (${closed.length})`}
          </Button>
          {showClosed && closed.map((item) => (
            <Stack key={item.id} direction="row" justifyContent="space-between" alignItems="center"
              sx={{ p: .7, borderBottom: `1px solid ${alpha('#94a3b8', .1)}` }}>
              <Typography sx={{ fontSize: '.78rem', color: '#94a3b8' }}>
                {item.source_kind === 'invoice' ? 'Factura' : 'Reserva'} · {item.expected_date} · {item.source_id.slice(0, 8)}…
              </Typography>
              <Button size="small" color="warning" disabled={exclusion.isPending}
                onClick={() => exclusion.mutate({ kind: item.source_kind, id: item.source_id })}>Desvincular</Button>
            </Stack>
          ))}
        </Box>
      )}
      <Typography sx={{ fontWeight: 800, mt: 2, mb: 1 }}>Movimientos previstos consolidados ({report.timeline.length})</Typography>
      {events.length === 0 && (
        <Typography sx={{ color: '#94a3b8', fontSize: '.83rem' }}>Sin movimientos futuros programados.</Typography>
      )}
      <Stack spacing={.55}>
        {events.map((item, index) => (
          <Stack key={`${item.kind}:${item.source_id}:${item.date}:${index}`}
            direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }}
            justifyContent="space-between" spacing={.6}
            sx={{ p: .95, borderRadius: 1.7, bgcolor: '#19273b' }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ color: '#f8fafc', fontSize: '.82rem', fontWeight: 750 }}>
                {item.date} · {item.name}
              </Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: '.72rem' }}>
                {item.kind === 'plan' ? 'Previsión manual' : item.kind === 'invoice' ? 'Factura pendiente' : 'Reserva pendiente'}
                {' · '}{item.account_name} · {item.financial_scope === 'personal' ? 'Personal' : 'Profesional'}
              </Typography>
            </Box>
            <Typography sx={{ color: item.movement_type === 'ingreso' ? GREEN : RED,
              fontWeight: 800, fontSize: '.86rem' }}>
              {item.movement_type === 'ingreso' ? '+' : '−'}{money(item.amount)}
            </Typography>
            <Typography sx={{ color: item.running_balance < 0 ? RED : YELLOW,
              fontSize: '.82rem', minWidth: 105, textAlign: { sm: 'right' } }}>
              Saldo {money(item.running_balance)}
            </Typography>
          </Stack>
        ))}
      </Stack>
      {report.timeline.length > 20 && (
        <Button size="small" sx={{ mt: 1 }} onClick={() => setShowAllEvents((v) => !v)}>
          {showAllEvents ? 'Ver menos' : `Ver los ${report.timeline.length} movimientos`}
        </Button>
      )}

      <Dialog open={Boolean(editor)} onClose={() => !mutation.isPending && setEditor(null)}
        fullWidth maxWidth="sm">
        <DialogTitle>{editor ? `Proyectar ${editor.kind === 'invoice' ? 'factura' : 'reserva'}` : 'Proyección'}</DialogTitle>
        {editor && <DialogContent>
          <Typography sx={{ fontWeight: 750, mb: 1.4 }}>{editor.name}</Typography>
          <Stack spacing={1.7}>
            <TextField label="Fecha estimada de cobro o pago" type="date" fullWidth
              InputLabelProps={{ shrink: true }} value={form.expected_date}
              onChange={(e) => setForm((f) => ({ ...f, expected_date: e.target.value }))}
              helperText="Es una estimación; un vencimiento no garantiza el cobro." />
            <FormControl fullWidth>
              <InputLabel>Cuenta prevista</InputLabel>
              <Select label="Cuenta prevista" value={form.account_id}
                disabled={editor.kind === 'reserve'}
                onChange={(e) => setForm((f) => ({ ...f, account_id: e.target.value }))}>
                {report.accounts.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name} · {a.financial_scope === 'professional' ? 'Profesional' : 'Personal'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Sustituir una previsión manual (opcional)</InputLabel>
              <Select label="Sustituir una previsión manual (opcional)"
                value={form.linked_plan_id && form.linked_occurrence_date
                  ? `${form.linked_plan_id}|${form.linked_occurrence_date}` : ''}
                onChange={(e) => selectedPlan(e.target.value)}>
                <MenuItem value="">No, este compromiso es adicional</MenuItem>
                {planOptions.map((plan: PVCashflowPlanOccurrence) => (
                  <MenuItem key={`${plan.plan_id}:${plan.occurrence_date}`}
                    value={`${plan.plan_id}|${plan.occurrence_date}`}>
                    {plan.occurrence_date} · {plan.name} · {money(plan.amount)}
                    {' · '}{plan.financial_scope === 'personal' ? 'Personal' : 'Profesional'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {form.linked_plan_id && <Alert severity="info">
              Esta fuente sustituye SOLO la ocurrencia seleccionada de la previsión manual.
              No se registra ni se elimina ningún movimiento bancario.
            </Alert>}
            {!form.linked_plan_id && <Alert severity="warning">
              Confirma que este compromiso no esté ya incluido en tus previsiones recurrentes
              o en otra factura o reserva; de lo contrario podría contarse dos veces.
            </Alert>}
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>}
        <DialogActions>
          <Button disabled={mutation.isPending} onClick={() => setEditor(null)}>Cancelar</Button>
          <Button disabled={saveDisabled} variant="contained" onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Guardando…' : 'Guardar proyección'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
